import { useRef } from 'react'
import {
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { UserDocument, DocumentType, DocumentStatus } from '../../types'
import {
  useUserDocuments,
  useUploadUserDocument,
  useDeleteUserDocument,
  useDownloadUserDocument,
} from '../../hooks/useDocuments'

interface UserDocumentationTabProps {
  passportNumber?: string
  onPassportNumberChange?: (value: string) => void
}

const DOC_CONFIG: Array<{
  type: DocumentType
  title: string
  description: string
  acceptLabel: string
}> = [
  {
    type: DocumentType.IC_PASSPORT,
    title: 'Passport / NRIC',
    description: 'Upload your passport or national identity card (IC).',
    acceptLabel: 'Image or PDF, max 10MB',
  },
  {
    type: DocumentType.PROOF_OF_ADDRESS,
    title: 'Proof of Address',
    description: 'Bank statement, utility/phone bill, or similar (within the last 3 months).',
    acceptLabel: 'Image or PDF, max 10MB',
  },
  {
    type: DocumentType.BIRTH_CERTIFICATE,
    title: 'Birth Certificate',
    description: 'Upload your birth certificate for identity verification.',
    acceptLabel: 'Image or PDF, max 10MB',
  },
]

export default function UserDocumentationTab({
  passportNumber,
  onPassportNumberChange,
}: UserDocumentationTabProps) {
  const { data: documents = [], isLoading } = useUserDocuments()
  const uploadMutation = useUploadUserDocument()
  const deleteMutation = useDeleteUserDocument()
  const downloadMutation = useDownloadUserDocument()

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Documentation</h3>
        <p className="text-sm text-gray-500 mt-1">
          Upload personal documents for review. Verified documents increase your profile completion.
        </p>
      </div>

      {DOC_CONFIG.map((cfg) => {
        const doc = documents.find(
          (d) =>
            d.type === cfg.type ||
            // Treat UTILITY_BILL and PROOF_OF_ADDRESS as the same slot
            (cfg.type === DocumentType.PROOF_OF_ADDRESS && d.type === DocumentType.UTILITY_BILL),
        )
        return (
          <DocumentCard
            key={cfg.type}
            config={cfg}
            doc={doc}
            uploading={uploadMutation.isPending}
            passportNumber={passportNumber}
            onPassportNumberChange={onPassportNumberChange}
            onUpload={(file) => uploadMutation.mutate({ file, documentType: cfg.type })}
            onDelete={() => doc && deleteMutation.mutate(doc.id)}
            onDownload={() =>
              doc &&
              downloadMutation.mutate({ id: doc.id, filename: doc.originalName || doc.fileName })
            }
            isDeleting={deleteMutation.isPending}
            isDownloading={downloadMutation.isPending}
          />
        )
      })}

      {isLoading && documents.length === 0 && (
        <p className="text-sm text-gray-500">Loading documents…</p>
      )}
    </div>
  )
}

function DocumentCard({
  config,
  doc,
  uploading,
  passportNumber,
  onPassportNumberChange,
  onUpload,
  onDelete,
  onDownload,
  isDeleting,
  isDownloading,
}: {
  config: (typeof DOC_CONFIG)[number]
  doc?: UserDocument
  uploading: boolean
  passportNumber?: string
  onPassportNumberChange?: (value: string) => void
  onUpload: (file: File) => void
  onDelete: () => void
  onDownload: () => void
  isDeleting: boolean
  isDownloading: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) onUpload(f)
    // reset so the same file can be re-selected later
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
              <DocumentTextIcon className="h-5 w-5" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">{config.title}</h4>
              <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
            </div>
          </div>
          {doc && <StatusBadge status={doc.status} />}
        </div>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Passport number input on the IC_PASSPORT card */}
        {config.type === DocumentType.IC_PASSPORT && onPassportNumberChange && (
          <div>
            <label
              htmlFor="passportNumber"
              className="block text-xs font-medium text-gray-700 uppercase tracking-wide"
            >
              Passport / NRIC Number
            </label>
            <input
              id="passportNumber"
              type="text"
              value={passportNumber ?? ''}
              onChange={(e) => onPassportNumberChange(e.target.value)}
              placeholder="e.g. A12345678 or 900101-14-5678"
              className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        )}

        {!doc ? (
          <div className="flex flex-col items-start gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100 disabled:opacity-60"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              {uploading ? 'Uploading…' : 'Upload document'}
            </button>
            <p className="text-xs text-gray-400">{config.acceptLabel}</p>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFile}
              className="hidden"
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {doc.originalName || doc.fileName}
                </p>
                <p className="text-xs text-gray-500">
                  {(doc.fileSize / 1024).toFixed(0)} KB · {formatUploaded(doc.uploadedAt)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onDownload}
                  disabled={isDownloading}
                  title="Download"
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
                {doc.status !== DocumentStatus.VERIFIED && (
                  <button
                    type="button"
                    onClick={onDelete}
                    disabled={isDeleting}
                    title="Delete"
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {doc.status === DocumentStatus.REJECTED && doc.verificationNotes && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-xs font-medium text-red-800">Reviewer note:</p>
                <p className="text-xs text-red-700 mt-0.5">{doc.verificationNotes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: DocumentStatus }) {
  const map: Record<
    DocumentStatus,
    { label: string; className: string; Icon: typeof CheckCircleIcon }
  > = {
    [DocumentStatus.PENDING]: {
      label: 'Pending',
      className: 'bg-yellow-50 text-yellow-700 ring-yellow-200',
      Icon: ClockIcon,
    },
    [DocumentStatus.UPLOADED]: {
      label: 'Pending review',
      className: 'bg-yellow-50 text-yellow-700 ring-yellow-200',
      Icon: ClockIcon,
    },
    [DocumentStatus.VERIFIED]: {
      label: 'Verified',
      className: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      Icon: CheckCircleIcon,
    },
    [DocumentStatus.REJECTED]: {
      label: 'Rejected',
      className: 'bg-red-50 text-red-700 ring-red-200',
      Icon: XCircleIcon,
    },
  }
  const { label, className, Icon } = map[status] ?? map[DocumentStatus.PENDING]
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  )
}

function formatUploaded(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}
